import React from 'react';
import { JcTab, JcTabs } from '../components/JcTabs';
import { JcBoxProps } from '../components/JcBox';
import { JcAppRoute } from './JcAppRoute';
import { JcAppRoutingConfig, RouteProps } from './JcAppRoutingConfig';
import { JcAppNavigation, useJcAppNavigation } from './JcAppNavigation';


/**
 * A tab whose state is URL-backed: instead of a manual `href` + `onTabChange`, it
 * declares its destination once via typed `routeProps` (validated against the tab
 * set's `route`). The component derives the `href` and does the SPA navigation.
 */
export interface JcRouteTab<PropsT, T extends string> extends Omit<JcTab<T>, "href"> {
    readonly routeProps: PropsT;
}

export interface JcRouteTabsProps<
    RoutesT extends Record<string, JcAppRoute<any, any>>,
    K extends keyof RoutesT,
    T extends string
> extends Omit<JcBoxProps, "children"> {
    readonly route: K;
    readonly activeTab: T;
    readonly tabs: JcRouteTab<RouteProps<RoutesT[K]>, T>[];
    /**
     * Push a new history entry instead of replacing. Tab changes default to
     * `replace` (an intra-page view change shouldn't pollute back-button history).
     */
    readonly push?: boolean;
}

/**
 * Creates a typed, route-aware tabs component bound to a routing config — the tab
 * analogue of `makeJcRouteLink`.
 *
 *   export const AppRouteTabs = makeJcRouteTabs(appRoutingConfig);
 *
 *   <AppRouteTabs route="SEASON" activeTab={tab} tabs={[
 *       { id: 'league', label: 'League', routeProps: { seasonId, tab: 'league' }, content: <League/> },
 *   ]} />
 *
 * Each tab header is a real `<a href>` (middle-/Cmd-click open it in a new tab),
 * plain clicks are routed via the SPA navigation hook, and the destination is
 * type-checked once — no `onTabChange`, no hand-written `href`.
 */
export function makeJcRouteTabs<RoutesT extends Record<string, JcAppRoute<any, any>>>(
    config: JcAppRoutingConfig<RoutesT>
) {
    return function JcRouteTabs<K extends keyof RoutesT, T extends string>(
        props: JcRouteTabsProps<RoutesT, K, T>
    ): React.ReactElement {
        const { navigateToRoute } = useJcAppNavigation(config);
        const { route, activeTab, tabs, push, ...boxProps } = props;

        const jcTabs: JcTab<T>[] = tabs.map(({ routeProps, ...tab }) => ({
            ...tab,
            href: JcAppNavigation.generatePath(config, route, routeProps),
        }));

        const handleTabChange = (id: T): void => {
            const tab = tabs.find(t => t.id === id);
            if (tab) navigateToRoute(route, tab.routeProps, { replace: !push });
        };

        return <JcTabs {...boxProps} tabs={jcTabs} activeTab={activeTab} onTabChange={handleTabChange} />;
    };
}
